import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import prepareAuthHeaders from "@utils/prepareAuthHeaders";
import { ListResponse } from "@models/list-response";
import { Reminder } from "@models/reminder";
import { ReminderEvent } from "@models/reminderEvent";
import { accountSetupApi } from "@store/accountSetupApi";
import { planApi } from "@store/planApi";

const BASE_API_URL = process.env.REACT_APP_API_URL;

export enum EventInterval {
  Day = "Day",
  Week = "Week",
  Month = "Month",
}

export interface GetReminderEventsParams {
  tz: string;
  date: string;
  interval: EventInterval;
  patientId: string;
}

export const reminderApi = createApi({
  reducerPath: "reminderApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${BASE_API_URL}/reminders`,
    prepareHeaders: prepareAuthHeaders,
  }),
  tagTypes: ["Reminder", "Event"],
  endpoints: (builder) => ({
    getReminders: builder.query<ListResponse<Reminder>, { patientId: string }>({
      providesTags: ["Reminder"],
      query: ({ patientId }) => {
        return {
          url: "",
          method: "GET",
          params: {
            patientId,
          },
        };
      },
    }),
    createReminder: builder.mutation<Reminder, Partial<Reminder>>({
      invalidatesTags: ["Reminder", "Event"],
      query: (body) => {
        return {
          url: "",
          method: "POST",
          body,
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            accountSetupApi.util.invalidateTags(["AccountSetupProgress"]),
          );
          dispatch(planApi.util.invalidateTags(["Subscription"]));
        } catch (e) {}
      },
    }),
    updateReminder: builder.mutation<
      Reminder,
      Partial<Reminder> & { id: string }
    >({
      invalidatesTags: ["Reminder", "Event"],
      query: ({ id, ...body }) => {
        return {
          url: id!,
          method: "PATCH",
          body,
        };
      },
    }),
    deleteReminder: builder.mutation<void, { id: string }>({
      invalidatesTags: ["Reminder", "Event"],
      query: ({ id }) => {
        return {
          url: id,
          method: "DELETE",
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            accountSetupApi.util.invalidateTags(["AccountSetupProgress"]),
          );
          dispatch(planApi.util.invalidateTags(["Subscription"]));
        } catch (e) {}
      },
    }),
    getReminderEvents: builder.query<
      ListResponse<ReminderEvent>,
      GetReminderEventsParams
    >({
      providesTags: ["Event"],
      query: (params) => {
        return {
          url: "events",
          method: "GET",
          params,
        };
      },
    }),
  }),
});

export const {
  useGetRemindersQuery,
  useCreateReminderMutation,
  useUpdateReminderMutation,
  useDeleteReminderMutation,
  useGetReminderEventsQuery,
} = reminderApi;
